import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { employeeService } from '../../../services/employeeService';

const CalculationEnginePanel = ({ minimumWage, onPayrollProcessed }) => {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [payrollData, setPayrollData] = useState({
    basePay: '',
    hoursWorked: '160',
    overtimeHours: '0',
    allowances: '0',
    deductions: '0'
  });
  const [calculatedPayroll, setCalculatedPayroll] = useState(null);
  const [calculating, setCalculating] = useState(false);

  useEffect(() => {
    loadEmployees();
  }, []);

  const loadEmployees = async () => {
    try {
      const data = await employeeService?.getAll();
      setEmployees(data || []);
    } catch (err) {
      console.error('Failed to load employees:', err);
    }
  };

  const handleEmployeeSelect = (e) => {
    const employeeId = e?.target?.value;
    const employee = employees?.find(emp => emp?.id === employeeId);
    setSelectedEmployee(employee);
    
    if (employee?.baseSalary) {
      const monthlyPay = employee?.baseSalary / 12;
      setPayrollData(prev => ({ ...prev, basePay: monthlyPay?.toFixed(2) }));
    }
  };

  const calculatePayroll = () => {
    setCalculating(true);
    
    try {
      const basePay = parseFloat(payrollData?.basePay) || 0;
      const hoursWorked = parseFloat(payrollData?.hoursWorked) || 0;
      const overtimeHours = parseFloat(payrollData?.overtimeHours) || 0;
      const allowances = parseFloat(payrollData?.allowances) || 0;
      const deductions = parseFloat(payrollData?.deductions) || 0;

      // Calculate hourly rate
      const hourlyRate = basePay / 160; // Assuming 160 hours per month

      // Overtime calculation (1.5x rate)
      const overtimePay = overtimeHours * hourlyRate * 1.5;

      // Gross pay
      const grossPay = basePay + overtimePay + allowances;

      // SWT calculation (progressive tax rates for PNG)
      let swtAmount = 0;
      const annualGross = grossPay * 12;
      
      if (annualGross <= 12500) {
        swtAmount = 0;
      } else if (annualGross <= 20000) {
        swtAmount = (annualGross - 12500) * 0.22;
      } else if (annualGross <= 33000) {
        swtAmount = 1650 + (annualGross - 20000) * 0.30;
      } else if (annualGross <= 70000) {
        swtAmount = 5550 + (annualGross - 33000) * 0.35;
      } else {
        swtAmount = 18500 + (annualGross - 70000) * 0.42;
      }
      swtAmount = swtAmount / 12; // Monthly SWT

      // NASFUND calculation (8.4% employer contribution)
      const employerSuper = grossPay * 0.084;
      const employeeSuper = grossPay * 0.06; // 6% employee contribution

      // Net pay
      const netPay = grossPay - swtAmount - employeeSuper - deductions;

      setCalculatedPayroll({
        basePay,
        overtimePay,
        allowances,
        grossPay,
        swtAmount,
        employeeSuper,
        employerSuper,
        deductions,
        netPay,
        hoursWorked,
        overtimeHours,
        hourlyRate
      });
    } catch (err) {
      console.error('Calculation error:', err);
    } finally {
      setCalculating(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setPayrollData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Automated Payroll Calculation Engine</h3>
        <p className="text-sm text-gray-600 mt-1">Calculate wages with PNG compliance (SWT, NASFUND, overtime)</p>
      </div>

      {/* Employee Selection */}
      <div className="bg-gray-50 rounded-lg p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Select Employee</label>
          <select
            onChange={handleEmployeeSelect}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          >
            <option value="">Choose an employee</option>
            {employees?.map((emp) => (
              <option key={emp?.id} value={emp?.id}>
                {emp?.firstName} {emp?.lastName} - {emp?.employeeNumber} ({emp?.employmentType})
              </option>
            ))}
          </select>
        </div>

        {selectedEmployee && (
          <div className="grid grid-cols-2 gap-4 p-4 bg-white rounded-lg border border-gray-200">
            <div>
              <p className="text-xs text-gray-600">Department</p>
              <p className="font-medium text-gray-900">{selectedEmployee?.department}</p>
            </div>
            <div>
              <p className="text-xs text-gray-600">Position</p>
              <p className="font-medium text-gray-900">{selectedEmployee?.position}</p>
            </div>
            <div>
              <p className="text-xs text-gray-600">Employment Type</p>
              <p className="font-medium text-gray-900">{selectedEmployee?.employmentType?.replace('_', ' ')?.toUpperCase()}</p>
            </div>
            <div>
              <p className="text-xs text-gray-600">Citizenship</p>
              <p className="font-medium text-gray-900">{selectedEmployee?.citizenship}</p>
            </div>
          </div>
        )}

        {/* Payroll Input Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Base Pay (PGK)</label>
            <Input
              type="number"
              name="basePay"
              value={payrollData?.basePay}
              onChange={handleInputChange}
              placeholder="Monthly base salary"
              step="0.01"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Hours Worked</label>
            <Input
              type="number"
              name="hoursWorked"
              value={payrollData?.hoursWorked}
              onChange={handleInputChange}
              placeholder="Regular hours"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Overtime Hours</label>
            <Input
              type="number"
              name="overtimeHours"
              value={payrollData?.overtimeHours}
              onChange={handleInputChange}
              placeholder="Overtime at 1.5x"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Allowances (PGK)</label>
            <Input
              type="number"
              name="allowances"
              value={payrollData?.allowances}
              onChange={handleInputChange}
              placeholder="Additional allowances"
              step="0.01"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Deductions (PGK)</label>
            <Input
              type="number"
              name="deductions"
              value={payrollData?.deductions}
              onChange={handleInputChange}
              placeholder="Other deductions"
              step="0.01"
            />
          </div>
        </div>

        <Button
          onClick={calculatePayroll}
          disabled={calculating || !payrollData?.basePay}
          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-3 rounded-lg font-medium flex items-center justify-center gap-2"
        >
          {calculating ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              Calculating...
            </>
          ) : (
            <>
              <Icon name="Calculator" className="h-5 w-5" />
              Calculate Payroll
            </>
          )}
        </Button>
      </div>

      {/* Calculation Results */}
      {calculatedPayroll && (
        <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-lg p-6 border-2 border-emerald-200">
          <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Icon name="CheckCircle" className="h-5 w-5 text-emerald-600" />
            Payroll Calculation Results
          </h4>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg p-4">
              <p className="text-xs text-gray-600 mb-1">Base Pay</p>
              <p className="text-lg font-bold text-gray-900">PGK {calculatedPayroll?.basePay?.toFixed(2)}</p>
            </div>
            <div className="bg-white rounded-lg p-4">
              <p className="text-xs text-gray-600 mb-1">Overtime Pay (1.5x)</p>
              <p className="text-lg font-bold text-gray-900">PGK {calculatedPayroll?.overtimePay?.toFixed(2)}</p>
            </div>
            <div className="bg-white rounded-lg p-4">
              <p className="text-xs text-gray-600 mb-1">Allowances</p>
              <p className="text-lg font-bold text-gray-900">PGK {calculatedPayroll?.allowances?.toFixed(2)}</p>
            </div>
            <div className="bg-white rounded-lg p-4 border-2 border-emerald-300">
              <p className="text-xs text-gray-600 mb-1">Gross Pay</p>
              <p className="text-lg font-bold text-emerald-600">PGK {calculatedPayroll?.grossPay?.toFixed(2)}</p>
            </div>
            <div className="bg-white rounded-lg p-4">
              <p className="text-xs text-gray-600 mb-1">SWT (Tax)</p>
              <p className="text-lg font-bold text-red-600">-PGK {calculatedPayroll?.swtAmount?.toFixed(2)}</p>
            </div>
            <div className="bg-white rounded-lg p-4">
              <p className="text-xs text-gray-600 mb-1">Employee Super (6%)</p>
              <p className="text-lg font-bold text-red-600">-PGK {calculatedPayroll?.employeeSuper?.toFixed(2)}</p>
            </div>
            <div className="bg-white rounded-lg p-4">
              <p className="text-xs text-gray-600 mb-1">Employer Super (8.4%)</p>
              <p className="text-lg font-bold text-blue-600">PGK {calculatedPayroll?.employerSuper?.toFixed(2)}</p>
            </div>
            <div className="bg-white rounded-lg p-4">
              <p className="text-xs text-gray-600 mb-1">Other Deductions</p>
              <p className="text-lg font-bold text-red-600">-PGK {calculatedPayroll?.deductions?.toFixed(2)}</p>
            </div>
            <div className="bg-emerald-600 text-white rounded-lg p-4 border-2 border-emerald-700">
              <p className="text-xs mb-1">Net Pay</p>
              <p className="text-xl font-bold">PGK {calculatedPayroll?.netPay?.toFixed(2)}</p>
            </div>
          </div>

          <div className="mt-4 p-4 bg-white rounded-lg border border-emerald-200">
            <p className="text-xs text-gray-600 mb-2">Calculation Summary:</p>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• Regular Hours: {calculatedPayroll?.hoursWorked} @ PGK {calculatedPayroll?.hourlyRate?.toFixed(2)}/hr</li>
              <li>• Overtime Hours: {calculatedPayroll?.overtimeHours} @ PGK {(calculatedPayroll?.hourlyRate * 1.5)?.toFixed(2)}/hr</li>
              <li>• NASFUND Total: PGK {(calculatedPayroll?.employeeSuper + calculatedPayroll?.employerSuper)?.toFixed(2)} (Employee + Employer)</li>
              <li>• Compliant with PNG Employment Act 1978 and minimum wage requirements</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalculationEnginePanel;